import {all, call, fork, put, select, takeLatest} from 'redux-saga/effects'
import {push} from 'react-router-redux'
import types from 'actions/types'
import {generalError} from 'actions/index'
import {
  addSubscriptionFailure,
  addSubscriptionSuccess,
  cancelSubscriptionFailure,
  cancelSubscriptionSuccess,
  getCancellationDateFailure,
  getCancellationDateSuccess,
  getClientSecretFailure,
  getClientSecretSuccess,
  getPriceFailure,
  getPriceSuccess,
  loadPaymentMethod,
  loadPaymentMethodFailure,
  loadPaymentMethodSuccess,
  loadUpcomingInvoiceFailure,
  loadUpcomingInvoiceSuccess,
  updatePaymentMethodFailure,
  updatePaymentMethodSuccess,
} from 'actions/subscriptions'
import {loginSuccess, sessionLoginAttempt} from 'actions/auth'
import {getRedirectUrl} from 'helpers/navigation'
import {loadAccountPackages} from 'actions/data'
import {snackbarMessage} from 'actions/snackbar'
import iconNames from 'theme/icons'
import {formatDate} from 'helpers/dates'
import {findPackagePartCodeForAccountPackage} from 'helpers/plans'
import {getPackages} from 'selectors/data'

export default (api) => {

  function* getClientSecret() {
    try {

      const response = yield call(api.subscriptions.getClientSecret)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(getClientSecretSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(getClientSecretFailure(message, code))
          })

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* addSubscription({paymentMethodId}) {
    try {
      const response = yield call(api.subscriptions.add, {paymentMethodId})
      yield api.handleResponse(
          response,
          function* onOk() {
            yield put(addSubscriptionSuccess())

            const result = yield call(api.userLoad)
            if (result.ok) {
              const user = result.data
              yield put(loginSuccess(user))

              const redirectUrl = getRedirectUrl(user)
              yield put(push(redirectUrl))
            }
          },
          function* onFail(key, message, code) {
            yield put(addSubscriptionFailure(message, code))
            yield put(snackbarMessage(message, 'error', false))
          })

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* cancelSubscription({cancellationReason, cancellationNotes}) {
    try {
      const response = yield call(api.subscriptions.cancel, {cancellationReason, cancellationNotes})
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const {paymentDue, cancelDate} = data.result

            yield put(sessionLoginAttempt())
            yield put(loadAccountPackages())

            yield put(cancelSubscriptionSuccess(paymentDue, cancelDate))

            const message = `Your account will be cancelled on ${formatDate(cancelDate, 'ddd D-MMM YYYY')}`
            yield put(snackbarMessage(message, iconNames.tick, false))
          },
          function* onFail(key, message, code) {
            yield put(cancelSubscriptionFailure(message, code))
          })

    } catch (e) {
      console.error(e)
      yield put(generalError('Failed to cancel account - please contact Clarity Support to cancel'))
    }
  }

  function* paymentMethodLoad({}) {
    try {
      const response = yield call(api.subscriptions.getPaymentMethod)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadPaymentMethodSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadPaymentMethodFailure(message, code))
          })

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* updatePaymentMethod({paymentMethodId, callback}) {
    try {

      const params = {paymentMethodId}
      const response = yield call(api.subscriptions.updatePaymentMethod, params)
      yield api.handleResponse(
          response,
          function* onOk() {
            yield put(loadPaymentMethod())
            callback(true)
            yield put(updatePaymentMethodSuccess())
            yield put(snackbarMessage('Your payment details were successfully updated', iconNames.tick))
          },
          function* onFail(key, message, code) {
            callback(false, message)
            yield put(updatePaymentMethodFailure(message, code))
            yield put(snackbarMessage('Your payment details could not be updated', iconNames.error, false))
          })

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  const getPackagesParam = (accountPackages, featurePackages) => {
    return accountPackages.map(accountPackage => (
        {
          partCode: findPackagePartCodeForAccountPackage(featurePackages, accountPackage),
          quantity: accountPackage.quantity,
        }
    ))
  }

  function* getPrice({accountPackages}) {
    try {
      const featurePackages = yield select(getPackages)

      const params = {
        packages: getPackagesParam(accountPackages, featurePackages),
      }

      const response = yield call(api.subscriptions.getPrice, params)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(getPriceSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(getPriceFailure(message, code))
          })

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* loadUpcomingInvoice() {

    try {

      const response = yield call(api.subscriptions.getUpcomingInvoice)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(loadUpcomingInvoiceSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(loadUpcomingInvoiceFailure(message, code))
          })

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* getCancellationDate() {
    try {

      const response = yield call(api.subscriptions.getCancellationDate)
      yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(getCancellationDateSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(getCancellationDateFailure(message, code))
          })

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* myWatcher() {
    yield all([
      takeLatest(types.SUBSCRIPTIONS_GET_CLIENT_SECRET_ATTEMPT, getClientSecret),
      takeLatest(types.SUBSCRIPTIONS_ADD_ATTEMPT, addSubscription),
      takeLatest(types.SUBSCRIPTIONS_CANCEL_ATTEMPT, cancelSubscription),
      takeLatest(types.SUBSCRIPTIONS_GET_PRICE_ATTEMPT, getPrice),
      takeLatest(types.PAYMENT_METHOD_LOAD_ATTEMPT, paymentMethodLoad),
      takeLatest(types.PAYMENT_METHOD_UPDATE_ATTEMPT, updatePaymentMethod),
      takeLatest(types.SUBSCRIPTIONS_LOAD_UPCOMING_INVOICE_ATTEMPT, loadUpcomingInvoice),
      takeLatest(types.SUBSCRIPTIONS_GET_CANCEL_DATE_ATTEMPT, getCancellationDate),
    ])
  }

  function* watcher() {
    yield fork(myWatcher)
  }

  return {
    watcher,
  }
}

